// Typed RPC layer. Business errors arrive either as {error:{code,message}}
// JSON (public RPCs, so rate-limit counters still commit) or as PostgREST
// errors whose message is the code and details the human text (owner RPCs).
import type { SupabaseClient } from '@supabase/supabase-js';
import type { z } from 'zod';
import { supabaseFor } from './supabase';
import {
  availabilitySchema,
  publicBookingSchema,
  tenantSchema,
  type Agenda,
  type Membership,
  type OwnerBooking,
  type OwnerSettings,
  type Stats,
} from './types';

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status = 400,
  ) {
    super(message);
  }
}

const NETWORK_MESSAGE = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';

export async function rpc<T>(client: SupabaseClient, fn: string, args: Record<string, unknown>, schema?: z.ZodType<T>): Promise<T> {
  let res;
  try {
    res = await client.rpc(fn, args);
  } catch {
    throw new ApiError('network', NETWORK_MESSAGE, 0);
  }
  const { data, error } = res;
  if (error) {
    if (!error.code && /fetch|network/i.test(error.message)) throw new ApiError('network', NETWORK_MESSAGE, 0);
    const human = error.details && !/^[a-z_]+$/.test(error.details) ? error.details : humanize(error.message);
    throw new ApiError(error.message, human, Number(String(error.code).replace(/^PT/, '')) || 400);
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    const e = (data as { error: { code: string; message: string; status: number } }).error;
    throw new ApiError(e.code, e.message, e.status);
  }
  return schema ? schema.parse(data) : (data as T);
}

function humanize(code: string): string {
  const map: Record<string, string> = {
    auth_required: 'Войдите в кабинет',
    forbidden: 'Нет доступа к этой студии',
    rate_limited: 'Слишком много запросов. Попробуйте чуть позже.',
  };
  return map[code] ?? 'Не получилось. Попробуйте ещё раз.';
}

// ---------------------------------------------------------------------------
// Public (client)
// ---------------------------------------------------------------------------
export const publicApi = (slug: string) => {
  const c = supabaseFor(slug);
  return {
    tenant: () => rpc(c, 'get_tenant_public', { p_slug: slug }, tenantSchema),
    availability: (serviceId: string, from: string | null, days = 7) =>
      rpc(c, 'get_availability', { p_slug: slug, p_service_id: serviceId, p_from: from, p_days: days }, availabilitySchema),
    createBooking: (input: {
      serviceId: string;
      startsAt: string;
      name: string;
      phone: string;
      car: string;
      comment: string;
      idempotencyKey: string;
      accessToken: string;
    }) =>
      rpc(
        c,
        'create_booking',
        {
          p_slug: slug,
          p_service_id: input.serviceId,
          p_starts_at: input.startsAt,
          p_customer_name: input.name,
          p_customer_phone: input.phone,
          p_car: input.car,
          p_comment: input.comment,
          p_idempotency_key: input.idempotencyKey,
          p_access_token: input.accessToken,
        },
        publicBookingSchema,
      ),
    booking: (token: string) => rpc(c, 'get_booking', { p_slug: slug, p_access_token: token }, publicBookingSchema),
    cancel: (token: string, reason = '') =>
      rpc(c, 'cancel_booking', { p_slug: slug, p_access_token: token, p_reason: reason }, publicBookingSchema),
    subscribePush: (token: string, sub: { endpoint: string; p256dh: string; auth: string }) =>
      rpc<{ subscribed: boolean; reminder_at: string | null; preview: boolean }>(c, 'subscribe_booking_push', {
        p_slug: slug,
        p_access_token: token,
        p_endpoint: sub.endpoint,
        p_p256dh: sub.p256dh,
        p_auth: sub.auth,
      }),
    unsubscribePush: (token: string) => rpc(c, 'unsubscribe_booking_push', { p_slug: slug, p_access_token: token }),
  };
};

// ---------------------------------------------------------------------------
// Owner
// ---------------------------------------------------------------------------
export const ownerApi = (slug: string, tenantId: string) => {
  const c = supabaseFor(slug);
  const t = { p_tenant_id: tenantId };
  return {
    agenda: (from: string, to: string) => rpc<Agenda>(c, 'owner_agenda', { ...t, p_from: from, p_to: to }),
    booking: (bookingId: string) => rpc<OwnerBooking>(c, 'owner_get_booking', { ...t, p_booking_id: bookingId }),
    availability: (serviceId: string, from: string | null, days = 7) =>
      rpc(c, 'owner_availability', { ...t, p_service_id: serviceId, p_from: from, p_days: days }, availabilitySchema),
    createBooking: (i: {
      serviceId: string;
      startsAt: string;
      resourceId: string | null;
      name: string;
      phone: string;
      car: string;
      comment: string;
      idempotencyKey: string;
      outsideHours: boolean;
    }) =>
      rpc<OwnerBooking>(c, 'owner_create_booking', {
        ...t,
        p_service_id: i.serviceId,
        p_starts_at: i.startsAt,
        p_resource_id: i.resourceId,
        p_customer_name: i.name,
        p_customer_phone: i.phone,
        p_car: i.car,
        p_comment: i.comment,
        p_idempotency_key: i.idempotencyKey,
        p_outside_hours: i.outsideHours,
      }),
    reschedule: (bookingId: string, startsAt: string, resourceId: string | null, outsideHours = false) =>
      rpc<OwnerBooking & { changed: boolean }>(c, 'owner_reschedule_booking', {
        ...t,
        p_booking_id: bookingId,
        p_starts_at: startsAt,
        p_resource_id: resourceId,
        p_outside_hours: outsideHours,
      }),
    cancel: (bookingId: string, reason: string) => rpc<OwnerBooking>(c, 'owner_cancel_booking', { ...t, p_booking_id: bookingId, p_reason: reason }),
    setStatus: (bookingId: string, status: string) =>
      rpc<OwnerBooking>(c, 'owner_set_booking_status', { ...t, p_booking_id: bookingId, p_status: status }),
    payment: (bookingId: string, kind: 'payment' | 'refund', amountCents: number, method: string, note: string, key: string) =>
      rpc<OwnerBooking>(c, 'owner_record_payment', {
        ...t,
        p_booking_id: bookingId,
        p_kind: kind,
        p_amount_cents: amountCents,
        p_method: method,
        p_note: note,
        p_idempotency_key: key,
      }),
    block: (resourceId: string, startsAt: string, endsAt: string, reason: string, key: string) =>
      rpc<{ id: string }>(c, 'owner_block_resource', {
        ...t,
        p_resource_id: resourceId,
        p_starts_at: startsAt,
        p_ends_at: endsAt,
        p_reason: reason,
        p_idempotency_key: key,
      }),
    removeBlock: (blockId: string) => rpc(c, 'owner_remove_block', { ...t, p_block_id: blockId }),
    stats: (period: string, anchor: string | null = null) => rpc<Stats>(c, 'owner_stats', { ...t, p_period: period, p_anchor: anchor }),
    settings: () => rpc<OwnerSettings>(c, 'owner_get_settings', t),
    updateProfile: (patch: Record<string, unknown>) => rpc<OwnerSettings>(c, 'owner_update_profile', { ...t, p_patch: patch }),
    upsertService: (s: Record<string, unknown>) => rpc<{ id: string }>(c, 'owner_upsert_service', { ...t, p_service: s }),
    upsertResource: (r: Record<string, unknown>) => rpc<{ id: string }>(c, 'owner_upsert_resource', { ...t, p_resource: r }),
    setHours: (hours: { weekday: number; opens: string; closes: string }[]) => rpc(c, 'owner_set_working_hours', { ...t, p_hours: hours }),
    setException: (day: string, isClosed: boolean, opens: string | null, closes: string | null, note: string) =>
      rpc<{ ok: boolean; bookings_on_day: number }>(c, 'owner_set_exception', {
        ...t,
        p_day: day,
        p_is_closed: isClosed,
        p_opens: opens,
        p_closes: closes,
        p_note: note,
      }),
    deleteException: (day: string) => rpc(c, 'owner_delete_exception', { ...t, p_day: day }),
    setInfoCard: (position: number, title: string, body: string, icon: string) =>
      rpc(c, 'owner_set_info_card', { ...t, p_position: position, p_title: title, p_body: body, p_icon: icon }),
    upsertPhoto: (p: Record<string, unknown>) => rpc<{ id: string }>(c, 'owner_upsert_photo', { ...t, p_photo: p }),
    deletePhoto: (id: string) => rpc<{ ok: boolean; storage_path: string | null }>(c, 'owner_delete_photo', { ...t, p_photo_id: id }),
    subscribePush: (sub: { endpoint: string; p256dh: string; auth: string }) =>
      rpc(c, 'owner_subscribe_push', { ...t, p_endpoint: sub.endpoint, p_p256dh: sub.p256dh, p_auth: sub.auth }),
    /** Upload into <tenant_id>/owner/ (enforced by Storage RLS + owner_upsert_photo). */
    uploadPhoto: async (file: Blob, name: string) => {
      const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
      const path = `${tenantId}/owner/${name}-${crypto.randomUUID().slice(0, 8)}.${ext}`;
      const { error } = await c.storage.from('tenant-media').upload(path, file, { contentType: file.type, upsert: false });
      if (error) throw new ApiError('upload_failed', 'Не удалось загрузить фото. Попробуйте ещё раз.');
      return path;
    },
  };
};

export async function myMemberships(slug: string) {
  return rpc<Membership[]>(supabaseFor(slug), 'my_memberships', {});
}

export type PublicApi = ReturnType<typeof publicApi>;
export type OwnerApi = ReturnType<typeof ownerApi>;
